// Firestore user profile service
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import { User } from "firebase/auth";
import { db, getSafeAvatar } from "./firebase";
import { UserProfile, UsageTracking } from "@/types/user";

const USERS_COLLECTION = "users";

// Email-based role mapping
// Security Note: Admin emails should be managed server-side (see server/middleware/auth_admin_email.ts)
// This client-side mapping is for UI/UX only - server validation is required
const ADMIN_EMAILS_ENV = import.meta.env.VITE_ADMIN_EMAILS || "philip@wemakerobot.com,hiseconomy@gmail.com";
const PREMIUM_EMAILS_ENV = import.meta.env.VITE_PREMIUM_EMAILS || "elearning@wemakerobot.com";
const PLATINUM_EMAILS_ENV = import.meta.env.VITE_PLATINUM_EMAILS || "ozknews.com@gmail.com";

// Build email-to-role map from environment variables
function buildEmailRoleMap(): Record<string, "admin" | "premium" | "platinum" | "free"> {
  const map: Record<string, "admin" | "premium" | "platinum" | "free"> = {};
  
  // Admin emails
  ADMIN_EMAILS_ENV.split(',').forEach((email: string) => {
    const trimmed = email.trim();
    if (trimmed) map[trimmed.toLowerCase()] = "admin";
  });
  
  // Premium emails
  PREMIUM_EMAILS_ENV.split(',').forEach((email: string) => {
    const trimmed = email.trim();
    if (trimmed) map[trimmed.toLowerCase()] = "premium";
  });
  
  // Platinum emails
  PLATINUM_EMAILS_ENV.split(',').forEach((email: string) => {
    const trimmed = email.trim();
    if (trimmed) map[trimmed.toLowerCase()] = "platinum";
  });
  
  // Test accounts (development only)
  if (import.meta.env.MODE === 'development') {
    map["test1@ozknews.com"] = "admin";
    map["test2@ozknews.com"] = "admin";
  }
  
  return map;
}

const EMAIL_ROLE_MAP = buildEmailRoleMap();

/**
 * Get role for email address
 */
function getRoleForEmail(email: string | null): "admin" | "premium" | "platinum" | "free" {
  if (!email) return "free";
  return EMAIL_ROLE_MAP[email.toLowerCase()] || "free";
}

/**
 * Check if email is an admin email
 */
function isAdminEmail(email: string | null): boolean {
  return getRoleForEmail(email) === "admin";
}

/**
 * Create or get user profile from Firestore
 */
export async function getOrCreateUserProfile(user: User): Promise<UserProfile> {
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const userRef = doc(db, USERS_COLLECTION, user.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const data = userSnap.data();
    
    // Determine role based on email mapping
    const emailRole = getRoleForEmail(user.email);
    let role = emailRole !== "free" ? emailRole : (data.role || "free");
    
    // Update Firestore if role changed (admin only)
    if (data.role !== role && role === "admin") {
      try {
        await updateDoc(userRef, { 
          role: role,
          subscriptionStatus: "active",
          updatedAt: serverTimestamp() 
        });
      } catch (error) {
        console.warn("Failed to update role:", error);
      }
    }
    
    return {
      uid: user.uid,
      email: data.email || user.email || "",
      displayName: data.displayName || user.displayName || "사용자",
      photoURL: getSafeAvatar(data.photoURL || user.photoURL),
      role,
      emailFrequency: data.emailFrequency || null,
      preferredCategories: data.preferredCategories || [],
      usageTracking: data.usageTracking || {
        wordLookups: 0,
        quizzesTaken: 0,
        lastReset: new Date().toISOString(),
      },
      stripeCustomerId: data.stripeCustomerId || null,
      stripeSubscriptionId: data.stripeSubscriptionId || null,
      subscriptionStatus: data.subscriptionStatus || null,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : new Date().toISOString(),
    };
  }

  // Document doesn't exist - server should have created it
  // Return profile from Auth data
  const now = new Date().toISOString();
  const role = getRoleForEmail(user.email);
  
  return {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || "사용자",
    photoURL: getSafeAvatar(user.photoURL),
    role: role,
    emailFrequency: null,
    preferredCategories: [],
    usageTracking: {
      wordLookups: 0,
      quizzesTaken: 0,
      lastReset: now,
    },
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    subscriptionStatus: (role === "premium" || role === "platinum") ? "active" : null,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Update user profile
 */
export async function updateUserProfile(
  uid: string, 
  updates: Partial<UserProfile>
): Promise<void> {
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const userRef = doc(db, USERS_COLLECTION, uid);
  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Check if user can use a feature (free tier limits)
 */
export function canUseFeature(
  profile: UserProfile, 
  feature: "wordLookup" | "quiz" | "saveWord"
): boolean {
  // Premium and admin users have unlimited access
  if (profile.role === "premium" || profile.role === "admin") return true;

  const { usageTracking } = profile;
  const lastReset = new Date(usageTracking.lastReset);
  const now = new Date();

  // Reset daily counters if needed
  const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24));
  
  if (feature === "wordLookup") {
    if (daysSinceReset >= 1) {
      // Will be reset on next increment
      return true;
    }
    return usageTracking.wordLookups < 5;
  }

  if (feature === "quiz") {
    const weeksSinceReset = Math.floor(daysSinceReset / 7);
    if (weeksSinceReset >= 1) {
      // Will be reset on next increment
      return true;
    }
    return usageTracking.quizzesTaken < 1;
  }

  if (feature === "saveWord") {
    // Check localStorage for saved words count
    const savedWords = JSON.parse(localStorage.getItem("vocabulary") || "[]");
    return savedWords.length < 10;
  }

  return false;
}

/**
 * Increment usage counter
 */
export async function incrementUsage(
  uid: string,
  feature: "wordLookup" | "quiz"
): Promise<void> {
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const userRef = doc(db, USERS_COLLECTION, uid);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) return;

  const data = userSnap.data();
  const usageTracking = data.usageTracking as UsageTracking;
  const lastReset = new Date(usageTracking.lastReset);
  const now = new Date();
  const daysSinceReset = Math.floor((now.getTime() - lastReset.getTime()) / (1000 * 60 * 60 * 24));

  let newUsageTracking = { ...usageTracking };

  if (feature === "wordLookup") {
    if (daysSinceReset >= 1) {
      // Reset daily counter
      newUsageTracking = {
        wordLookups: 1,
        quizzesTaken: usageTracking.quizzesTaken,
        lastReset: now.toISOString(),
      };
    } else {
      newUsageTracking.wordLookups += 1;
    }
  }

  if (feature === "quiz") {
    const weeksSinceReset = Math.floor(daysSinceReset / 7);
    if (weeksSinceReset >= 1) {
      // Reset weekly counter
      newUsageTracking = {
        wordLookups: usageTracking.wordLookups,
        quizzesTaken: 1,
        lastReset: now.toISOString(),
      };
    } else {
      newUsageTracking.quizzesTaken += 1;
    }
  }

  await updateDoc(userRef, {
    usageTracking: newUsageTracking,
    updatedAt: serverTimestamp(),
  });
}
